// src/shared/stores/useSettingsStore.ts
import { create } from 'zustand';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/config';
import { getAndSanitize } from '../../shared/utils/sanitization';

interface CvuConfig {
  name: string;
  number: string;
  holder: string;
  active: boolean;
  startTime: string | null;
  endTime: string | null;
}

interface CvuTimelineItem {
  time: string;
  activeCvu: (CvuConfig & { configName: string }) | null;
  isCurrent: boolean;
}

interface CvuTimelineResponse {
  timeline: CvuTimelineItem[];
  currentTime: string;
  configs: Record<string, CvuConfig>;
  systemEnabled: boolean;
}

interface ActiveCvuResponse {
  success: boolean;
  data?: {
    name: string;
    number: string;
    holder: string;
    configName: string;
    activeFrom: string;
    activeTo: string;
    currentTime: string;
  };
  message?: string;
}

interface CvuSystemStatus {
  enabled: boolean;
}

interface SettingsState {
  settings: any | null;
  isLoading: boolean;
  error: Error | null;
  lastFetched: number | null;
  
  // CVU-specific state
  cvuConfigs: Record<string, CvuConfig>;
  cvuTimeline: CvuTimelineItem[];
  activeCvu: ActiveCvuResponse | null;
  cvuSystemStatus: CvuSystemStatus;
  cvuLoading: boolean;
  cvuError: Error | null;
  
  // Original methods
  fetchSettings: (adminIdOverride?: string | null) => Promise<any>;
  updateSettings: (values: any) => Promise<any>;
  clearSettings: () => void;
  
  // CVU methods
  getActiveCvu: () => Promise<ActiveCvuResponse>;
  getCvuConfigs: () => Promise<{ cvuConfigs: Record<string, CvuConfig>; cvuSystemEnabled: boolean }>;
  getCvuTimeline: () => Promise<CvuTimelineResponse>;
  upsertCvuConfig: (configName: string, configData: CvuConfig) => Promise<{ success: boolean; data?: CvuConfig }>;
  deleteCvuConfig: (configName: string) => Promise<{ success: boolean }>;
  enableCvuSystem: () => Promise<{ success: boolean; data?: CvuSystemStatus }>;
  disableCvuSystem: () => Promise<{ success: boolean; data?: CvuSystemStatus }>;
  getCvuSystemStatus: () => Promise<CvuSystemStatus>;
  setCvuConfigs: (configs: Record<string, CvuConfig>) => void;
  setCvuTimeline: (timeline: CvuTimelineItem[]) => void;
  setActiveCvu: (cvu: ActiveCvuResponse | null) => void;
  setCvuSystemStatus: (status: CvuSystemStatus) => void;
  clearCvuData: () => void;
}

// Cache time: 5 minutes
const CACHE_TIME = 5 * 60 * 1000;

// Track ongoing request to prevent duplicates
let currentFetch: Promise<any> | null = null;
let currentCvuFetch: Promise<any> | null = null;

// Helper function to process CVU configs and convert escaped newlines
const processCvuConfigs = (configs: Record<string, CvuConfig>): Record<string, CvuConfig> => {
  const processed: Record<string, CvuConfig> = {};

  for (const [key, config] of Object.entries(configs)) {
    const originalHolder = config.holder;

    // Debug logging

    // Process holder to convert various escape formats to actual newlines
    let processedHolder = originalHolder;
    if (processedHolder && typeof processedHolder === 'string') {
      // Replace double-escaped newlines (\\n) with actual newlines
      // This handles the case where JSON double-escapes: "BUENAS\\n\\nBIEN?" → "BUENAS\n\nBIEN?"
      processedHolder = processedHolder
        .replace(/\\\\r\\\\n/g, '\n')  // Double-escaped Windows: \\r\\n → \n
        .replace(/\\\\n/g, '\n')        // Double-escaped Unix: \\n → \n
        .replace(/\\\\r/g, '\n')        // Double-escaped Mac: \\r → \n
        .replace(/\\r\\n/g, '\n')       // Single-escaped Windows: \r\n → \n
        .replace(/\\n/g, '\n')          // Single-escaped Unix: \n → \n
        .replace(/\\r/g, '\n');         // Single-escaped Mac: \r → \n

    }

    processed[key] = {
      ...config,
      holder: processedHolder
    };
  }

  return processed;
};

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: null,
  isLoading: false,
  error: null,
  lastFetched: null,
  
  // CVU-specific state
  cvuConfigs: {},
  cvuTimeline: [],
  activeCvu: null,
  cvuSystemStatus: { enabled: true },
  cvuLoading: false,
  cvuError: null,
  
  fetchSettings: async (adminIdOverride?: string | null) => {
    const { settings, lastFetched } = get();
    const currentTime = Date.now();

    // If adminIdOverride is provided, skip cache (we need fresh data for the selected admin)
    if (!adminIdOverride) {
      // Return cached data if fresh
      if (
        settings &&
        lastFetched &&
        currentTime - lastFetched < CACHE_TIME
      ) {
        return settings;
      }

      // Reuse in-flight request if exists
      if (currentFetch) {
        return currentFetch;
      }
    }

    set({ isLoading: true, error: null });

    try {
      const token = getAndSanitize('token');
      const userAdminId = getAndSanitize('userId');

      // Use override if provided, otherwise use logged in user's ID
      const adminId = adminIdOverride || userAdminId;

      if (!token) throw new Error('No token found');


      // Store promise to deduplicate concurrent requests (only when not using override)
      const fetchPromise = new Promise(async (resolve, reject) => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/settings`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Admin-ID': adminId,
            },
            params: {
              adminId,
            },
          });

          if (!response.data.success) {
            throw new Error(response.data.message || 'Error fetching settings');
          }


          // Only update cache if not using override
          if (!adminIdOverride) {
            set({
              settings: response.data,
              isLoading: false,
              lastFetched: Date.now()
            });
          } else {
            set({ isLoading: false });
          }

          resolve(response.data);
        } catch (error) {
          const typedError = error as Error;
          set({ isLoading: false, error: typedError });
          reject(typedError);
        } finally {
          if (!adminIdOverride) {
            currentFetch = null;
          }
        }
      });

      if (!adminIdOverride) {
        currentFetch = fetchPromise;
      }

      return await fetchPromise;
    } catch (error) {
      console.error('[SettingsStore] Error fetching settings:', error);
      return null;
    }
  },
  
  updateSettings: async (values: any) => {
    set({ isLoading: true, error: null });
    
    try {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token) throw new Error('No token found');
      
      
      const response = await axios.put(
        `${API_BASE_URL}/api/settings`,
        { ...values, adminId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Admin-ID': adminId,
          },
        }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error updating settings');
      }
      
      
      const updatedSettings = {
        ...response.data,
        data: {
          ...(response.data.data || {}),
          ...values
        }
      };
      
      set({ 
        settings: updatedSettings,
        isLoading: false,
        lastFetched: Date.now()
      });
      
      return updatedSettings;
    } catch (error) {
      const typedError = error as Error;
      set({ isLoading: false, error: typedError });
      throw error;
    }
  },
  
  clearSettings: () => {
    set({ 
      settings: null, 
      lastFetched: null 
    });
  },

  // CVU Methods
  getActiveCvu: async (): Promise<ActiveCvuResponse> => {
    const { activeCvu } = get();
    
    if (activeCvu && activeCvu.success) {
    }

    if (currentCvuFetch) {
      return currentCvuFetch;
    }

    set({ cvuLoading: true, cvuError: null });

    try {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token || !adminId) throw new Error('No authentication');


      currentCvuFetch = new Promise(async (resolve) => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/settings/cvu/active`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Admin-ID': adminId,
            },
          });

          const result = response.data as ActiveCvuResponse;
          
          set({ 
            activeCvu: result,
            cvuLoading: false
          });

          resolve(result);
        } catch (error) {
          const typedError = error as Error;
          const errorResponse: ActiveCvuResponse = {
            success: false,
            message: typedError.message
          };
          
          set({ 
            activeCvu: errorResponse,
            cvuLoading: false, 
            cvuError: typedError 
          });
          
          resolve(errorResponse);
        } finally {
          currentCvuFetch = null;
        }
      });

      return await currentCvuFetch;
    } catch (error) {
      console.error('[SettingsStore] Error getting active CVU:', error);
      const errorResponse: ActiveCvuResponse = {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      };
      return errorResponse;
    }
  },

  getCvuConfigs: async () => {
    set({ cvuLoading: true, cvuError: null });

    try {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token || !adminId) throw new Error('No authentication');


      const response = await axios.get(`${API_BASE_URL}/api/settings/cvu/configs`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Admin-ID': adminId,
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error fetching CVU configs');
      }

      const configs = response.data.data;

      // Process configs to convert escaped newlines to actual line breaks
      const processedConfigs = processCvuConfigs(configs.cvuConfigs || {});

      set({
        cvuConfigs: processedConfigs,
        cvuSystemStatus: { enabled: configs.cvuSystemEnabled ?? true },
        cvuLoading: false
      });

      return {
        ...configs,
        cvuConfigs: processedConfigs
      };
    } catch (error) {
      const typedError = error as Error;
      set({ cvuLoading: false, cvuError: typedError });
      throw error;
    }
  },

  getCvuTimeline: async (): Promise<CvuTimelineResponse> => {
    set({ cvuLoading: true, cvuError: null });

    try {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token || !adminId) throw new Error('No authentication');


      const response = await axios.get(`${API_BASE_URL}/api/settings/cvu/timeline`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Admin-ID': adminId,
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error fetching CVU timeline');
      }

      const timelineData = response.data.data as CvuTimelineResponse;

      // Process configs to convert escaped newlines to actual line breaks
      const processedConfigs = processCvuConfigs(timelineData.configs || {});

      set({
        cvuTimeline: timelineData.timeline,
        cvuConfigs: processedConfigs,
        cvuSystemStatus: { enabled: timelineData.systemEnabled ?? true },
        cvuLoading: false
      });

      return {
        ...timelineData,
        configs: processedConfigs
      };
    } catch (error) {
      const typedError = error as Error;
      set({ cvuLoading: false, cvuError: typedError });
      throw error;
    }
  },

  upsertCvuConfig: async (configName: string, configData: CvuConfig) => {
    set({ cvuLoading: true, cvuError: null });

    try {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');

      if (!token || !adminId) throw new Error('No authentication');

      const payload = { configName, ...configData };
      console.log('[SettingsStore] Sending CVU config to backend:', JSON.stringify(payload, null, 2));

      const response = await axios.post(`${API_BASE_URL}/api/settings/cvu/config`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Admin-ID': adminId,
        },
      });


      if (!response.data.success) {
        throw new Error(response.data.message || 'Error saving CVU configuration');
      }

      const { cvuConfigs } = get();
      const updatedConfigs = {
        ...cvuConfigs,
        [configName]: configData
      };


      set({
        cvuConfigs: updatedConfigs,
        cvuLoading: false
      });

      return response.data;
    } catch (error) {
      console.error('[SettingsStore] ❌ Error upserting CVU config:', error);
      const typedError = error as Error;
      set({ cvuLoading: false, cvuError: typedError });
      throw error;
    }
  },

  deleteCvuConfig: async (configName: string) => {
    set({ cvuLoading: true, cvuError: null });

    try {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token || !adminId) throw new Error('No authentication');


      const response = await axios.delete(`${API_BASE_URL}/api/settings/cvu/config/${configName}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Admin-ID': adminId,
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error deleting CVU configuration');
      }

      const { cvuConfigs } = get();
      const updatedConfigs = { ...cvuConfigs };
      delete updatedConfigs[configName];

      set({ 
        cvuConfigs: updatedConfigs,
        cvuLoading: false
      });

      return response.data;
    } catch (error) {
      const typedError = error as Error;
      set({ cvuLoading: false, cvuError: typedError });
      throw error;
    }
  },

  enableCvuSystem: async () => {
    set({ cvuLoading: true, cvuError: null });

    try {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token || !adminId) throw new Error('No authentication');


      const response = await axios.post(`${API_BASE_URL}/api/settings/cvu/enable`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Admin-ID': adminId,
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error enabling CVU system');
      }

      set({ 
        cvuSystemStatus: { enabled: true },
        cvuLoading: false
      });

      return response.data;
    } catch (error) {
      const typedError = error as Error;
      set({ cvuLoading: false, cvuError: typedError });
      throw error;
    }
  },

  disableCvuSystem: async () => {
    set({ cvuLoading: true, cvuError: null });

    try {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token || !adminId) throw new Error('No authentication');


      const response = await axios.post(`${API_BASE_URL}/api/settings/cvu/disable`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Admin-ID': adminId,
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error disabling CVU system');
      }

      set({ 
        cvuSystemStatus: { enabled: false },
        cvuLoading: false
      });

      return response.data;
    } catch (error) {
      const typedError = error as Error;
      set({ cvuLoading: false, cvuError: typedError });
      throw error;
    }
  },

  getCvuSystemStatus: async (): Promise<CvuSystemStatus> => {
    try {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token || !adminId) throw new Error('No authentication');

      const response = await axios.get(`${API_BASE_URL}/api/settings/cvu/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Admin-ID': adminId,
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error getting CVU system status');
      }

      const status = response.data.data as CvuSystemStatus;
      
      set({ cvuSystemStatus: status });

      return status;
    } catch (error) {
      const typedError = error as Error;
      set({ cvuError: typedError });
      throw error;
    }
  },

  // Local state setters
  setCvuConfigs: (configs: Record<string, CvuConfig>) => {
    set({ cvuConfigs: configs });
  },

  setCvuTimeline: (timeline: CvuTimelineItem[]) => {
    set({ cvuTimeline: timeline });
  },

  setActiveCvu: (cvu: ActiveCvuResponse | null) => {
    set({ activeCvu: cvu });
  },

  setCvuSystemStatus: (status: CvuSystemStatus) => {
    set({ cvuSystemStatus: status });
  },

  clearCvuData: () => {
    set({ 
      cvuConfigs: {},
      cvuTimeline: [],
      activeCvu: null,
      cvuSystemStatus: { enabled: true },
      cvuError: null
    });
  },
}));